import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { IconSvgColor, IconSvgName, IconSvgSize } from './icon.model';
import { SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { IconFetchService } from './icon-fetch.service';

/**
 * @usage
 * In your HTML <rx-icon name="credit-card" size="md" fill="teal"/>
 * In your project.json file add the path to the icons folder /libs/shared/rx-components/assets/images
 */
@Component({
  standalone: true,
  selector: 'rx-icon',
  imports: [CommonModule],
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
})
export class IconSvgComponent implements OnChanges {
  /**
   * @description Name of the icon, can be any icon from the icon list
   */
  @Input() name: IconSvgName | string;

  /**
   * @default 'md'
   * @property xs = 0.5rem; sm = 0.75rem; md = 1rem; lg = 1.5rem; xl = 2rem;
   * @description Size of the icon, large icons can have more details hence
   * when using xs, sm or md it will load add.svg where as lg and xl will load add-large.svg
   */
  @Input() size: IconSvgSize | string = IconSvgSize.md;

  /**
   * @default 'text-color' it will inherit the color of the parent's text
   * @description Color of the icon, can be any color from the color palette.
   */
  @Input() fill: IconSvgColor | string = IconSvgColor.textColor;

  /**
   * @default 'none'
   * @description Stroke color of the icon, can be any color from the color palette.
   * When the color is not specified, it will be none
   */
  @Input() stroke: IconSvgColor | string = IconSvgColor.none;

  // flip icon horizontally
  @Input() flipHorizontal = false;

  // flip icon vertically
  @Input() flipVertical = false;

  // rotate the icon 0 - 360 degrees
  @Input() rotate?: number;

  /**
   * @default 'assets/images/icons'
   * @description Path to the icons folder, this path must be specified in the project.json file in the assets array
   */
  @Input() path = 'assets/images/icons';

  /**
   * @default 'inline-block'
   * @description Display of the icon, can be any valid css display value
   */
  @Input() display = 'inline-block';

  /**
   * @default 'false'
   * @description Changes the margin-top property to center the icon in the box
   */
  @Input() centerIcon = false;

  iconElement$: Observable<SafeHtml>;
  #iconFetchService = inject(IconFetchService);

  get iconClasses(): string {
    const prefix = 'icon--';
    const classes: string[] = [];

    if (this.flipHorizontal) classes.push(`${prefix}flip-horizontal`);
    if (this.flipVertical) classes.push(`${prefix}flip-vertical`);
    if (this.fill) classes.push(`${prefix}fill-${this.fill}`);
    if (this.stroke) classes.push(`${prefix}stroke-${this.stroke}`);
    if (this.size) classes.push(`${prefix}${this.size}`);
    if (this.centerIcon) classes.push(`center-icon`);

    return classes.join(' ');
  }

  get rotation(): string {
    return this.rotate ? `${this.rotate}deg` : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name'] || changes['size']) {
      const iconFullPath = this.#getSvgIcon(<IconSvgName>this.name, <IconSvgSize>this.size);
      this.iconElement$ = this.#iconFetchService.getIcon(iconFullPath);
    }
  }

  #getSvgIcon(iconName: IconSvgName, iconSize: IconSvgSize): string {
    const iconFile = this.#getIconFile(iconName, iconSize);
    const iconFullImagePath = this.#getIconFullPath(iconFile);
    return iconFullImagePath;
  }

  #getIconFile(iconName: IconSvgName, size: IconSvgSize): string {
    return size === IconSvgSize.lg || size === IconSvgSize.xl ? `${iconName}-large.svg` : `${iconName}.svg`;
  }

  #getIconFullPath(iconFile: string): string {
    return `${this.path}/${iconFile}`;
  }
}
